import React from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from 'date-fns';

interface ImageQuotaPieChartProps {
  imagesAllowed: number | null;
  imagesCurrent: number | null;
  isFleetMode?: boolean;
  contractYears?: number;
  currentYear?: number;
  nextRenewalDate?: Date | null;
}

export const ImageQuotaPieChart = ({
  imagesAllowed,
  imagesCurrent,
  isFleetMode = false,
  contractYears,
  currentYear,
  nextRenewalDate
}: ImageQuotaPieChartProps) => {
  // Handle null values - show empty chart if data is missing
  if (imagesAllowed === null || imagesCurrent === null) {
    return <div className="bg-gray-100 rounded-lg p-1 shadow-sm relative w-[40px] h-[40px] flex items-center justify-center">
        <span className="text-[10px] font-medium text-gray-400">—</span>
      </div>;
  }
  const allowed = imagesAllowed;
  const current = imagesCurrent;
  const used = current;
  const remaining = Math.max(0, allowed - current);
  const data = [{
    name: 'Used',
    value: used
  }, {
    name: 'Remaining',
    value: remaining || 1
  } // Ensure at least 1 to show empty chart
  ];
  const COLORS = ['#0ea5e9', '#22c55e'];

  const isMultiYear = contractYears && contractYears > 1;

  return <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="bg-white rounded-lg p-1 shadow-sm relative">
            <PieChart width={40} height={40}>
              <Pie data={data} innerRadius={12} outerRadius={18} paddingAngle={2} dataKey="value" strokeWidth={0}>
                {data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
              </Pie>
            </PieChart>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-medium text-neutral-700">
                {remaining}
              </span>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs space-y-1">
            {isFleetMode && <p className="font-semibold text-blue-600 pb-1 border-b mb-1">
                Credit Pooling Enabled
              </p>}
            <p><span className="text-muted-foreground">Used:</span> <span className="font-medium">{used}</span></p>
            <p><span className="text-muted-foreground">Remaining:</span> <span className="font-medium text-green-600">{remaining}</span></p>
            <p><span className="text-muted-foreground">Quota:</span> <span className="font-medium">{allowed}</span></p>
            
            {isMultiYear && (
              <>
                <div className="border-t pt-1 mt-1">
                  <p><span className="text-muted-foreground">Contract:</span> <span className="font-medium">Year {currentYear} of {contractYears}</span></p>
                  {nextRenewalDate && (
                    <p><span className="text-muted-foreground">Next Renewal:</span> <span className="font-medium text-blue-600">{format(nextRenewalDate, 'MMM yyyy')}</span></p>
                  )}
                </div>
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>;
};